<template>
    <fullscreen-drawer
        v-if="featureFlags['live-site-responsive-design']"
        :visible="visible"
        title="Select Your Country"
        @close="handleClose"
    >
        <g-row type="flex" justify="space-around" class="g-mt-9">
            <g-column :span="24" class="g-mb-6">
                <g-typography tag="p" text-align="center" color="grey-6">
                    This should match the country your taxes are filed in.
                </g-typography>
            </g-column>

            <g-column :xs="24" :sm="13" class="g-mb-3">
                <g-select
                    v-model="country"
                    show-search
                    option-filter-prop="children"
                    class="country notranslate"
                    @change="handleCountrySelected"
                >
                    <g-select-option v-for="i in priorityCountries" :key="i.name" :value="i.name" class="notranslate">
                        {{ i.name }}
                    </g-select-option>
                    <g-select-option v-if="alphabeticalCountries.length > 0" key="separator" disabled>
                        ---------
                    </g-select-option>
                    <g-select-option v-for="i in alphabeticalCountries" :key="i.name" :value="i.name" class="notranslate">
                        {{ i.name }}
                    </g-select-option>
                </g-select>
            </g-column>

            <g-column :span="24" class="g-mb-6">
                <g-typography tag="p" size="small" text-align="center" color="grey-6">
                    *This helps to determine your tax and payout settings.
                </g-typography>
            </g-column>

            <g-column class="submit-row" :xs="24" :sm="24">
                <g-button
                    class="submit"
                    theme="live"
                    shape="round"
                    type="primary"
                    size="large"
                    :loading="isLoading"
                    :disabled="isLoading"
                    @click="handleSubmit"
                >
                    {{ submitButtonText }}
                </g-button>
            </g-column>
        </g-row>
    </fullscreen-drawer>

    <g-drawer
        v-else
        :visible="visible"
        :wrap-style="wrapStyle"
        height="100%"
        get-container="#app"
        placement="bottom"
        @close="handleClose"
    >
        <g-row type="flex" justify="space-around" class="g-mt-9">
            <g-column :span="24" class="g-mb-5">
                <g-typography tag="h1" font-weight="semibold" text-align="center">
                    Select your country
                </g-typography>
            </g-column>

            <g-column :span="24" class="g-mb-6">
                <g-typography tag="p" text-align="center" color="grey-6">
                    This should match the country your taxes are filed in.
                </g-typography>
            </g-column>

            <g-column :xs="24" :sm="13" class="g-mb-3">
                <g-select
                    v-model="country"
                    class="country notranslate"
                    show-search
                    option-filter-prop="children"
                    @change="handleCountrySelected"
                >
                    <g-select-option v-for="i in priorityCountries" :key="i.name" :value="i.name" class="notranslate">
                        {{ i.name }}
                    </g-select-option>
                    <g-select-option v-if="alphabeticalCountries.length > 0" key="separator" disabled>
                        ---------
                    </g-select-option>
                    <g-select-option v-for="i in alphabeticalCountries" :key="i.name" :value="i.name" class="notranslate">
                        {{ i.name }}
                    </g-select-option>
                </g-select>
            </g-column>

            <g-column :span="24" class="g-mb-6">
                <g-typography tag="p" size="small" text-align="center" color="grey-6">
                    *This helps to determine your tax and payout settings.
                </g-typography>
            </g-column>

            <g-column class="submit-row" :xs="24" :sm="24">
                <g-button
                    class="submit"
                    theme="live"
                    shape="round"
                    type="primary"
                    size="large"
                    :loading="isLoading"
                    :disabled="isLoading"
                    @click="handleSubmit"
                >
                    {{ submitButtonText }}
                </g-button>
            </g-column>
        </g-row>
    </g-drawer>
</template>

<script>
    import find from 'lodash-es/find';
    import { computed, ref, reactive, onUpdated, onMounted } from 'vue-demi';
    import { PAYOUT_PROVIDER_STRIPE, STEP_TAX_INFO_ID } from '../../../../../app-v2/constants/payouts';
    import {
        EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SELECTED,
        EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SUBMITTED,
        EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_CLOSED
    } from '../../../../../app-v2/constants/analytics-events';
    import useToggle from '../../../../composables/use-toggle';
    import useOnboarding from '../../../../composables/use-onboarding';
    import useMessage from '../../../../composables/use-message';
    import useLocations from '../../../../composables/use-locations';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useContactStore from '../../../../../stores/contact';
    import { useFeatureFlagsStore } from '../../../../../stores';
    import FullscreenDrawer from '../../../../../app-v2/components/library/FullscreenDrawer';

    export default {
        name: 'CountrySelection',
        components: { FullscreenDrawer },
        props: {
            visible: {
                type: Boolean,
                default: false
            }
        },

        setup(props, context) {
            const { message } = useMessage();
            const events = useEventTracking();
            const contact = useContactStore().data;
            const featureFlags = useFeatureFlagsStore().data;

            const wrapStyle = reactive({
                position: 'absolute'
            });

            const { countries, getCountries } = useLocations();
            const country = ref(contact.primary_address?.country);

            const priorityCountries = computed(() => {
                return countries.value.slice(0, 13);
            });

            const alphabeticalCountries = computed(() => {
                return countries.value.slice(13, countries.value.length + 1);
            });

            const selectedCountry = computed(() => {
                return find(countries.value, (i) => {
                    return i.name === country.value;
                });
            });

            onMounted(() => {
                getCountries();
            });

            onUpdated(() => {
                country.value ||= contact.primary_address?.country;
            });

            const { currentValue: isLoading, setTrue: loading, setFalse: doneLoading } = useToggle(false);

            const submitButtonText = computed(() => {
                return isLoading.value ? 'Loading' : 'Set up Tax Information';
            });

            const { createAccount } = useOnboarding();

            const handleSubmit = async () => {
                if (!selectedCountry.value?.code) {
                    message.error('Please choose your country of residence.');
                    return;
                }

                loading();

                try {
                    let account = null;
                    if (!contact?.payout_account_id) {
                        const accountRes = await createAccount(contact.id, selectedCountry.value?.code);
                        account = accountRes?.data;
                        if (!account) {
                            message.error('Oops! The account could not be created.');
                            events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SUBMITTED, getEventPayload(), false);
                            return;
                        }
                        contact.payout_account_id = account.id;
                        events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SUBMITTED, getEventPayload());
                    }
                    if (account?.provider !== PAYOUT_PROVIDER_STRIPE) {
                        context.emit('open-trolley-onboarding-drawer', true);
                        return;
                    }
                    context.emit('open-onboarding-drawer', STEP_TAX_INFO_ID, true);
                } catch (e) {
                    message.error(`Oops! Something went wrong: ${e}`);
                    events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SUBMITTED, getEventPayload(), false);
                    doneLoading();
                }
            };

            const handleClose = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_CLOSED, getEventPayload());
                context.emit('close');
            };

            const handleCountrySelected = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_SELECTED, getEventPayload());
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    country: selectedCountry.value?.code
                };
            };

            return {
                wrapStyle,
                submitButtonText,
                country,
                priorityCountries,
                alphabeticalCountries,
                selectedCountry,
                handleSubmit,
                handleClose,
                handleCountrySelected,
                isLoading,
                featureFlags,
            };
        }
    };
</script>

<style lang="scss" scoped>
    .country {
        width: 100%;
    }
    .submit {
        @media only screen and (max-width: $g-screen-size-xs) {
            padding: 0 8rem !important;
            width: 100%;
        }
        @media only screen and (min-width: $g-screen-size-xs) {
            padding: 0 1.5rem !important;
        }
    }
    .submit-row {
        display: flex;
        justify-content: center;
    }
</style>
